import axios from "axios";
import { API_BASE_URL } from "../apiConstants";
import { getAuthHeader } from "./authHeader";

const AI_API_BASE = `${API_BASE_URL}/ai`;
const ASSISTANT_API_BASE = `${API_BASE_URL}/assistant`;
const INSIGHTS_API_BASE = `${API_BASE_URL}/insights`;

const requestConfig = (config = {}) => ({
  ...getAuthHeader(),
  withCredentials: true,
  ...config,
});

const unwrap = (response) => response?.data?.data ?? response?.data;

const unwrapPage = (response) => ({
  data: Array.isArray(response?.data?.data) ? response.data.data : [],
  meta: response?.data?.meta ?? {},
});

/**
 * @typedef {"current" | "stale" | "redacted"} AssistantHistoryState
 * @typedef {{type: string, id: string, timestamp: string, version: string,
 * historyState: "current" | "stale", provenance?: string,
 * recordType?: string, recordId?: string, sourceTimestamp?: string,
 * sourceVersion?: string, sourceRef?: {type: string, id?: string}}} AssistantSource
 * @typedef {{id: string, conversationId: string, role: string, content: string,
 * historyState: AssistantHistoryState, sources: AssistantSource[], insertedAt: string}} AssistantMessage
 */

/** @returns {AssistantMessage} */
const normalizeMessage = (message) => {
  if (!message || !["current", "stale", "redacted"].includes(message.historyState)) {
    throw new Error("Message history state was missing or invalid.");
  }
  const redacted = message.historyState === "redacted";
  if (!redacted && (!Array.isArray(message.sources) ||
      message.sources.some((source) => !["current", "stale"].includes(source?.historyState)))) {
    throw new Error("Citation history state was missing or invalid.");
  }
  return {
    id: message.id,
    conversationId: message.conversationId,
    role: redacted ? "system" : message.role,
    content: redacted ? "" : message.content,
    historyState: message.historyState,
    sources: redacted ? [] : message.sources,
    insertedAt: message.insertedAt,
  };
};

const throwApiError = (error, fallback) => {
  const payload = error?.response?.data;
  const message =
    payload?.error?.message ||
    payload?.error ||
    payload?.message ||
    payload?.errors?.detail ||
    error?.message ||
    fallback;
  const apiError = new Error(message);
  apiError.status = error?.response?.status;
  apiError.code = payload?.error?.code || payload?.code || error?.code;
  apiError.cause = error;
  throw apiError;
};

/** Explicitly generate and persist a prediction; callers must read the stored result. */
export const generateDealPrediction = async (dealId) => {
  try {
    const response = await axios.post(
      `${INSIGHTS_API_BASE}/deals/${encodeURIComponent(dealId)}/score`,
      {},
      requestConfig(),
    );
    return unwrap(response);
  } catch (error) {
    throwApiError(error, "Deal prediction generation failed.");
  }
};

/** Queue a real call analysis. The response is a queued job, not a fabricated result. */
export const getCallAnalysis = async (callData) => {
  try {
    const response = await axios.post(
      `${AI_API_BASE}/call-analysis`,
      callData,
      requestConfig(),
    );
    return unwrap(response);
  } catch (error) {
    throwApiError(error, "Call analysis could not be queued.");
  }
};

export const getStoredCallAnalysis = async (callId) => {
  try {
    const response = await axios.get(
      `${AI_API_BASE}/analysis/${encodeURIComponent(callId)}`,
      requestConfig(),
    );
    return unwrap(response);
  } catch (error) {
    throwApiError(error, "Stored call analysis is unavailable.");
  }
};

export const getStoredDealPrediction = async (dealId) => {
  try {
    const response = await axios.get(
      `${INSIGHTS_API_BASE}/deals/${encodeURIComponent(dealId)}/prediction`,
      requestConfig(),
    );
    return unwrap(response);
  } catch (error) {
    throwApiError(error, "Stored deal prediction is unavailable.");
  }
};

export const getCoachingInsights = async (userId) => {
  try {
    const response = await axios.get(
      `${INSIGHTS_API_BASE}/coaching/${encodeURIComponent(userId)}`,
      requestConfig(),
    );
    return unwrap(response);
  } catch (error) {
    throwApiError(error, "Coaching insights are unavailable.");
  }
};


export const assistantApi = {
  getIdentity: async () => {
    try {
      return unwrap(
        await axios.get(ASSISTANT_API_BASE, requestConfig()),
      );
    } catch (error) {
      throwApiError(error, "Your assistant could not be loaded.");
    }
  },

  updateIdentity: async (settings) => {
    try {
      return unwrap(
        await axios.patch(ASSISTANT_API_BASE, settings, requestConfig()),
      );
    } catch (error) {
      throwApiError(error, "Assistant settings could not be saved.");
    }
  },

  listConversations: async (params = {}) => {
    try {
      return unwrapPage(
        await axios.get(
          `${ASSISTANT_API_BASE}/conversations`,
          requestConfig({ params }),
        ),
      );
    } catch (error) {
      throwApiError(error, "Conversation history could not be loaded.");
    }
  },

  createConversation: async (title) => {
    try {
      return unwrap(
        await axios.post(
          `${ASSISTANT_API_BASE}/conversations`,
          title?.trim() ? { title: title.trim() } : {},
          requestConfig(),
        ),
      );
    } catch (error) {
      throwApiError(error, "A conversation could not be created.");
    }
  },

  deleteConversation: async (conversationId) => {
    try {
      return unwrap(
        await axios.delete(
          `${ASSISTANT_API_BASE}/conversations/${encodeURIComponent(conversationId)}`,
          requestConfig(),
        ),
      );
    } catch (error) {
      throwApiError(error, "The conversation could not be deleted.");
    }
  },

  listMessages: async (conversationId, params = {}) => {
    try {
      const page = unwrapPage(
        await axios.get(
          `${ASSISTANT_API_BASE}/conversations/${encodeURIComponent(conversationId)}/messages`,
          requestConfig({ params }),
        ),
      );
      return { ...page, data: page.data.map(normalizeMessage) };
    } catch (error) {
      throwApiError(error, "Messages could not be loaded.");
    }
  },

  sendMessage: async (conversationId, content, clientMessageId) => {
    try {
      const result = unwrap(
        await axios.post(
          `${ASSISTANT_API_BASE}/conversations/${encodeURIComponent(conversationId)}/messages`,
          { content, clientMessageId },
          requestConfig(),
        ),
      );
      return { ...result, message: normalizeMessage(result.message) };
    } catch (error) {
      throwApiError(error, "Your message was not sent.");
    }
  },

  getRun: async (runId) => {
    try {
      return unwrap(
        await axios.get(
          `${ASSISTANT_API_BASE}/runs/${encodeURIComponent(runId)}`,
          requestConfig(),
        ),
      );
    } catch (error) {
      throwApiError(error, "Assistant run state could not be refreshed.");
    }
  },

  cancelRun: async (runId) => {
    try {
      return unwrap(
        await axios.post(
          `${ASSISTANT_API_BASE}/runs/${encodeURIComponent(runId)}/cancel`,
          {},
          requestConfig(),
        ),
      );
    } catch (error) {
      throwApiError(error, "The assistant run could not be cancelled.");
    }
  },

  listMemories: async (params = {}) => {
    try {
      return unwrapPage(
        await axios.get(
          `${ASSISTANT_API_BASE}/memories`,
          requestConfig({ params }),
        ),
      );
    } catch (error) {
      throwApiError(error, "Private memory could not be loaded.");
    }
  },

  createMemory: async (memory) => {
    try {
      return unwrap(
        await axios.post(
          `${ASSISTANT_API_BASE}/memories`,
          memory,
          requestConfig(),
        ),
      );
    } catch (error) {
      throwApiError(error, "The memory could not be saved.");
    }
  },

  updateMemory: async (memoryId, correction) => {
    try {
      return unwrap(
        await axios.patch(
          `${ASSISTANT_API_BASE}/memories/${encodeURIComponent(memoryId)}`,
          correction,
          requestConfig(),
        ),
      );
    } catch (error) {
      throwApiError(error, "The memory could not be corrected.");
    }
  },

  deleteMemory: async (memoryId) => {
    try {
      return unwrap(
        await axios.delete(
          `${ASSISTANT_API_BASE}/memories/${encodeURIComponent(memoryId)}`,
          requestConfig(),
        ),
      );
    } catch (error) {
      throwApiError(error, "The memory could not be forgotten.");
    }
  },

  approveAction: async (actionId) => {
    try {
      return unwrap(
        await axios.post(
          `${ASSISTANT_API_BASE}/actions/${encodeURIComponent(actionId)}/approve`,
          {},
          requestConfig(),
        ),
      );
    } catch (error) {
      throwApiError(error, "The action could not be approved.");
    }
  },

  rejectAction: async (actionId) => {
    try {
      return unwrap(
        await axios.post(
          `${ASSISTANT_API_BASE}/actions/${encodeURIComponent(actionId)}/reject`,
          {},
          requestConfig(),
        ),
      );
    } catch (error) {
      throwApiError(error, "The action could not be rejected.");
    }
  },
};

export default assistantApi;
